import { apiClient } from './apiClient'
import type { NotificationPreferencesData, NotificationPreferenceUpdate } from '../types/notifications'

export type WatchlistEntityType = 'company' | 'person'

export interface WatchlistItem {
  id: number
  listIds: number[]
  entityType: WatchlistEntityType
  entityId: string
  displayName: string
  createdAt: string
  /** ISO country code when entity address is outside Denmark. */
  countryCode?: string | null
  notificationPreferences?: NotificationPreferencesData
}

export interface WatchlistList {
  id: number
  name: string
  /** Parent list when nested (e.g. a party under »Folketinget«); null at the top level. */
  parentId?: number | null
  sortOrder: number
  createdAt: string
  items: WatchlistItem[]
}

export interface WatchlistData {
  lists: WatchlistList[]
  uncategorized: WatchlistItem[]
}

interface ApiResponse<T> {
  success: boolean
  data?: T
  error?: string
}

function parseError(err: unknown, fallback: string): Error {
  const ax = err as { response?: { data?: { error?: string } }; message?: string }
  return new Error(ax.response?.data?.error || ax.message || fallback)
}

export async function fetchWatchlist(): Promise<WatchlistData> {
  try {
    const res = await apiClient.get<ApiResponse<WatchlistData>>('/watchlist')
    const data = res.data.data
    return data ?? { lists: [], uncategorized: [] }
  } catch (e) {
    throw parseError(e, 'Kunne ikke hente watchlist')
  }
}

export async function addToWatchlist(
  entityType: WatchlistEntityType,
  entityId: string,
  displayName: string,
  listIds: number[] = [],
  countryCode?: string | null
): Promise<WatchlistItem> {
  try {
    const res = await apiClient.post<ApiResponse<WatchlistItem>>('/watchlist', {
      entityType,
      entityId,
      displayName,
      listIds,
      ...(countryCode ? { countryCode } : {}),
    })
    if (!res.data.success || !res.data.data) {
      throw new Error(res.data.error || 'Kunne ikke tilføje')
    }
    return res.data.data
  } catch (e) {
    throw parseError(e, 'Kunne ikke tilføje til watchlist')
  }
}

export async function removeFromWatchlist(id: number): Promise<void> {
  try {
    await apiClient.delete(`/watchlist/${id}`)
  } catch (e) {
    throw parseError(e, 'Kunne ikke fjerne fra watchlist')
  }
}

export async function createWatchlistList(name: string): Promise<WatchlistList> {
  try {
    const res = await apiClient.post<ApiResponse<WatchlistList>>('/watchlist/lists', {
      name,
    })
    if (!res.data.success || !res.data.data) {
      throw new Error(res.data.error || 'Kunne ikke oprette liste')
    }
    return { ...res.data.data, items: [] }
  } catch (e) {
    throw parseError(e, 'Kunne ikke oprette liste')
  }
}

export async function renameWatchlistList(id: number, name: string): Promise<WatchlistList> {
  try {
    const res = await apiClient.patch<ApiResponse<WatchlistList>>(
      `/watchlist/lists/${id}`,
      { name }
    )
    if (!res.data.success || !res.data.data) {
      throw new Error(res.data.error || 'Kunne ikke omdøbe liste')
    }
    return res.data.data
  } catch (e) {
    throw parseError(e, 'Kunne ikke omdøbe liste')
  }
}

export async function deleteWatchlistList(id: number): Promise<void> {
  try {
    await apiClient.delete(`/watchlist/lists/${id}`)
  } catch (e) {
    throw parseError(e, 'Kunne ikke slette liste')
  }
}

/** Save order and nesting of every list; `lists` is in display order. */
export async function saveWatchlistListTree(
  lists: Array<{ id: number; parentId: number | null }>
): Promise<WatchlistList[]> {
  try {
    const res = await apiClient.put<ApiResponse<WatchlistList[]>>('/watchlist/lists/tree', {
      lists,
    })
    if (!res.data.success || !res.data.data) {
      throw new Error(res.data.error || 'Kunne ikke flytte listen')
    }
    return res.data.data
  } catch (e) {
    throw parseError(e, 'Kunne ikke flytte listen')
  }
}

export async function setWatchlistItemLists(
  itemId: number,
  listIds: number[]
): Promise<WatchlistItem> {
  try {
    const res = await apiClient.patch<ApiResponse<WatchlistItem>>(`/watchlist/${itemId}`, {
      listIds,
    })
    if (!res.data.success || !res.data.data) {
      throw new Error(res.data.error || 'Kunne ikke opdatere lister')
    }
    return res.data.data
  } catch (e) {
    throw parseError(e, 'Kunne ikke opdatere lister')
  }
}

export async function removeFromWatchlistLists(
  itemId: number,
  listIds: number[]
): Promise<void> {
  try {
    await apiClient.patch(`/watchlist/${itemId}`, { removeListIds: listIds })
  } catch (e) {
    throw parseError(e, 'Kunne ikke fjerne fra lister')
  }
}

export async function updateWatchlistItemNotificationPreferences(
  itemId: number,
  preferences: NotificationPreferenceUpdate[]
): Promise<WatchlistItem> {
  try {
    const res = await apiClient.patch<ApiResponse<WatchlistItem>>(`/watchlist/${itemId}`, {
      notificationPreferences: { preferences },
    })
    if (!res.data.success || !res.data.data) {
      throw new Error(res.data.error || 'Kunne ikke gemme notifikationer')
    }
    return res.data.data
  } catch (e) {
    throw parseError(e, 'Kunne ikke gemme notifikationsindstillinger')
  }
}

/** Unique items across lists and uncategorized. */
export function flattenWatchlist(data: WatchlistData): WatchlistItem[] {
  const seen = new Set<number>()
  const out: WatchlistItem[] = []
  const push = (item: WatchlistItem) => {
    if (seen.has(item.id)) return
    seen.add(item.id)
    out.push(item)
  }
  for (const item of data.uncategorized) push(item)
  for (const list of data.lists) {
    for (const item of list.items) push(item)
  }
  return out
}

export function findWatchlistItem(
  data: WatchlistData,
  entityType: WatchlistEntityType,
  entityId: string
): WatchlistItem | undefined {
  const id = String(entityId)
  return flattenWatchlist(data).find(
    (i) => i.entityType === entityType && i.entityId === id
  )
}

/** Merge an updated item into watchlist data without refetching. */
export function patchWatchlistItem(
  data: WatchlistData,
  updated: WatchlistItem
): WatchlistData {
  const patch = (item: WatchlistItem) =>
    item.id === updated.id ? { ...item, ...updated } : item

  return {
    lists: data.lists.map((list) => ({
      ...list,
      items: list.items.map(patch),
    })),
    uncategorized: data.uncategorized.map(patch),
  }
}

/** Remove an item from all lists and uncategorized buckets. */
export function removeWatchlistItemFromData(
  data: WatchlistData,
  itemId: number
): WatchlistData {
  return {
    lists: data.lists.map((list) => ({
      ...list,
      items: list.items.filter((item) => item.id !== itemId),
    })),
    uncategorized: data.uncategorized.filter((item) => item.id !== itemId),
  }
}

/** Place an item in the correct buckets based on `listIds`. */
export function applyWatchlistItem(
  data: WatchlistData,
  item: WatchlistItem
): WatchlistData {
  const base = removeWatchlistItemFromData(data, item.id)
  if (item.listIds.length === 0) {
    return { ...base, uncategorized: [item, ...base.uncategorized] }
  }
  return {
    ...base,
    lists: base.lists.map((list) =>
      item.listIds.includes(list.id)
        ? { ...list, items: [item, ...list.items] }
        : list
    ),
  }
}

export function insertWatchlistList(
  data: WatchlistData,
  list: WatchlistList
): WatchlistData {
  return {
    ...data,
    lists: [...data.lists, { ...list, items: list.items ?? [] }],
  }
}

export function patchWatchlistListMeta(
  data: WatchlistData,
  list: Pick<WatchlistList, 'id' | 'name' | 'sortOrder' | 'createdAt' | 'parentId'>
): WatchlistData {
  return {
    ...data,
    lists: data.lists.map((entry) =>
      entry.id === list.id ? { ...entry, ...list } : entry
    ),
  }
}

/** Mirror backend behaviour when a list is deleted. */
export function removeWatchlistListFromData(
  data: WatchlistData,
  listId: number
): WatchlistData {
  const list = data.lists.find((entry) => entry.id === listId)
  if (!list) return data

  // Sublists move up into the deleted list's place.
  let next: WatchlistData = {
    lists: data.lists
      .filter((entry) => entry.id !== listId)
      .map((entry) =>
        entry.parentId === listId ? { ...entry, parentId: list.parentId ?? null } : entry
      ),
    uncategorized: [...data.uncategorized],
  }

  for (const item of list.items) {
    const newListIds = item.listIds.filter((id) => id !== listId)
    next = applyWatchlistItem(next, { ...item, listIds: newListIds })
  }

  return next
}

export function listNamesForItem(
  lists: WatchlistList[],
  listIds: number[]
): string[] {
  return listIds
    .map((lid) => lists.find((l) => l.id === lid)?.name)
    .filter((n): n is string => Boolean(n))
}
